import Phaser from 'phaser';
import type { PerkDefinition } from '@snake-royalle/shared';
import { sfx } from '../audio/SFX.js';
import { playContext as playMusic } from '../audio/Music.js';
import { getRandomPerks } from '../data/perks.js';
import { enableMenuScroll } from '../utils/menu-scroll.js';
import { track, trackDemoStarted, trackPlayButtonClicked } from '../analytics.js';
import { measureLatency, LAG_WARN_MS } from '../network/lag-check.js';

interface PortalEntry {
  x: number;
  y: number;
  radius: number;
  container: Phaser.GameObjects.Container;
  glowGfx: Phaser.GameObjects.Graphics;
  imgNormal: Phaser.GameObjects.Image | null;
  imgGlow: Phaser.GameObjects.Image | null;
  baseScale: number;
  color: number;
}

export class MainMenuScene extends Phaser.Scene {
  private portals: PortalEntry[] = [];

  constructor() {
    super({ key: 'MainMenuScene' });
  }

  preload(): void {
    if (!this.textures.exists('crupier_circle')) {
      this.load.image('crupier_circle', 'assets/sprites/menus/crupier_circle.webp');
    }
    if (!this.textures.exists('crupier_circle_glow')) {
      this.load.image('crupier_circle_glow', 'assets/sprites/menus/crupier_circle_glow.webp');
    }
    if (!this.textures.exists('mirror_circle')) {
      this.load.image('mirror_circle', 'assets/sprites/menus/mirror_circle.webp');
    }
    if (!this.textures.exists('mirror_circle_glow')) {
      this.load.image('mirror_circle_glow', 'assets/sprites/menus/mirror_circle_glow.webp');
    }
    if (!this.textures.exists('snakeai_circle')) {
      this.load.image('snakeai_circle', 'assets/sprites/menus/snakeai_circle.webp');
    }
    if (!this.textures.exists('snakeai_circle_glow')) {
      this.load.image('snakeai_circle_glow', 'assets/sprites/menus/snakeai_circle_glow.webp');
    }
    if (!this.textures.exists('crupier')) {
      this.load.image('crupier', 'assets/sprites/menus/snakecrupier.webp');
    }
    if (!this.textures.exists('banner')) {
      this.load.image('banner', 'assets/sprites/menus/banner.webp');
    }
  }

  create(): void {
    this.scene.stop('GameScene');
    this.scene.stop('HUDScene');
    // Music is lazy-loaded after the menu renders so boot is not blocked.
    // playContext('menu') is idempotent and cache-existence checks in
    // Music.ts mean it's safe to call before tracks are downloaded — it
    // becomes a no-op until lazyLoadMenuMusic resolves.
    this.lazyLoadMenuMusic();
    playMusic(this.game, 'menu');
    this.cameras.main.setZoom(1);
    this.cameras.main.setScroll(0, 0);
    this.cameras.main.fadeIn(500);
    this.portals = [];

    // Restore menu chrome — scroll, leaderboard, scroll hint, and the
    // wallet pill. Previous sibling scenes (Skin, PerkSelect, Game, etc.)
    // call disableMenuScroll() on enter which hides #user-profile; this
    // helper undoes that so the pill reappears when we come back to the
    // main menu. (The inline block below is redundant with enableMenuScroll
    // but kept harmless for clarity.)
    enableMenuScroll();
    window.scrollTo(0, 0);

    const lbSection = document.getElementById('leaderboard-section');
    const scrollHint = document.getElementById('scroll-hint');
    if (lbSection) lbSection.style.display = 'block';
    if (scrollHint) {
      scrollHint.style.display = '';
      scrollHint.classList.add('on-main-menu');
      scrollHint.classList.remove('hidden');
    }

    const { width, height } = this.scale;
    const cx = width / 2;

    // Background
    this.add.rectangle(cx, height / 2, width, height, 0x030308).setDepth(0);

    // Particles
    if (!this.textures.exists('menu_dust')) {
      const g = this.add.graphics();
      g.fillStyle(0xffffff, 1);
      g.fillRect(0, 0, 2, 2);
      g.generateTexture('menu_dust', 2, 2);
      g.destroy();
    }
    this.add.particles(cx, height / 2, 'menu_dust', {
      x: { min: -width / 2, max: width / 2 },
      y: { min: -height / 2, max: height / 2 },
      scale: { min: 0.2, max: 0.6 },
      alpha: { start: 0.3, end: 0 },
      lifespan: { min: 4000, max: 8000 },
      speed: { min: 3, max: 10 },
      angle: { min: 260, max: 280 },
      frequency: 150,
      quantity: 1,
      tint: [0x9d00ff, 0xffd700],
      blendMode: 'ADD',
    }).setDepth(1).setScrollFactor(0);

    // Banner logo
    if (this.textures.exists('banner')) {
      const banner = this.add.image(cx, height * 0.18, 'banner').setOrigin(0.5).setDepth(20);
      // Scale to fit ~40% of screen width
      const bannerScale = (width * 0.4) / banner.width;
      banner.setScale(bannerScale);
    } else {
      // Fallback text
      this.add.text(cx, height * 0.1, 'PUMP MY SNAKE', {
        fontFamily: 'monospace', fontSize: '42px', color: '#ffd700',
        stroke: '#664400', strokeThickness: 3, fontStyle: 'bold',
      }).setOrigin(0.5).setDepth(20);
    }

    // Portals
    const radius = Math.min(150, Math.min(width, height) * 0.25);
    const spacing = Math.min(360, (width - 40) / 3);
    const portalY = height * 0.56;

    const configs = [
      { label: 'PLAY', sub: 'Enter the Arena', color: 0xffd700, imgKey: 'crupier_circle', imgGlowKey: 'crupier_circle_glow' },
      { label: 'SKINS', sub: 'Customize Your Snake', color: 0x9d00ff, imgKey: 'mirror_circle', imgGlowKey: 'mirror_circle_glow' },
      { label: 'AGENTS', sub: 'Connect your AI Agent', color: 0x00ccff, imgKey: 'snakeai_circle', imgGlowKey: 'snakeai_circle_glow' },
    ];

    configs.forEach((cfg, i) => {
      const px = cx + (i - 1) * spacing;
      this.time.delayedCall(300 + i * 150, () => {
        const p = this.makePortal(px, portalY, radius, cfg, i);
        this.portals.push(p);
        // Entrance animation
        p.container.setAlpha(0).setScale(0.85);
        this.tweens.add({ targets: p.container, alpha: 1, scale: 1, duration: 500, ease: 'Back.easeOut' });
        if (p.imgNormal) {
          p.imgNormal.setAlpha(0);
          this.tweens.add({ targets: p.imgNormal, alpha: 1, duration: 600, delay: 100 });
        }
      });
    });

    // === TRY DEMO button — onboarding for crypto-skeptics ===
    // Sits below the portals, secondary visual weight (not competing with PLAY).
    const demoY = portalY + radius + 80;
    const demoBtn = this.add.text(cx, demoY, '▶  TRY DEMO  (no wallet, free)', {
      fontFamily: 'monospace', fontSize: '15px', color: '#ffd166',
      stroke: '#000', strokeThickness: 4, fontStyle: 'bold',
      padding: { x: 18, y: 8 },
    }).setOrigin(0.5).setDepth(20).setAlpha(0)
      .setInteractive({ useHandCursor: true });
    const demoSub = this.add.text(cx, demoY + 22, 'Try the full game with zero friction', {
      fontFamily: 'monospace', fontSize: '10px', color: '#666677',
    }).setOrigin(0.5).setDepth(20).setAlpha(0);

    this.tweens.add({ targets: [demoBtn, demoSub], alpha: 1, duration: 500, delay: 900 });
    this.tweens.add({
      targets: demoBtn, scale: 1.04, duration: 1100, yoyo: true, repeat: -1,
      delay: 1500, ease: 'Sine.easeInOut',
    });

    demoBtn.on('pointerover', () => { sfx(this, 'menu_portal_hover'); demoBtn.setColor('#ffffff'); });
    demoBtn.on('pointerout', () => demoBtn.setColor('#ffd166'));
    demoBtn.on('pointerdown', () => {
      trackDemoStarted();
      sfx(this, 'menu_portal_click');
      this.cameras.main.fadeOut(300, 0, 0, 0);
      this.cameras.main.once('camerafadeoutcomplete', () => {
        this.routeToGame({ perks: this.getRandomPerks(), demoMode: true });
      });
    });

    // Version
    this.add.text(width - 10, height - 10, 'v0.1.0', {
      fontFamily: 'monospace', fontSize: '10px', color: '#222233',
    }).setOrigin(1, 1).setDepth(20);
  }

  private makePortal(
    x: number, y: number, radius: number,
    cfg: { label: string; sub: string; color: number; imgKey: string; imgGlowKey: string },
    index: number
  ): PortalEntry {
    // Glow (behind, hidden)
    const glowGfx = this.add.graphics().setDepth(7).setAlpha(0);
    for (let r = 8; r >= 0; r--) {
      const p = r / 8;
      glowGfx.fillStyle(cfg.color, (1 - p) * 0.2);
      glowGfx.fillCircle(x, y, (radius + 50) * p);
    }

    // Images (pre-cropped circular — no mask needed)
    let imgNormal: Phaser.GameObjects.Image | null = null;
    let imgGlow: Phaser.GameObjects.Image | null = null;
    let baseScale = 0;

    if (cfg.imgKey && this.textures.exists(cfg.imgKey)) {
      baseScale = (radius * 2) / 256;

      imgNormal = this.add.image(x, y, cfg.imgKey)
        .setScale(baseScale).setDepth(9).setAlpha(1);

      if (cfg.imgGlowKey && this.textures.exists(cfg.imgGlowKey)) {
        imgGlow = this.add.image(x, y, cfg.imgGlowKey)
          .setScale(baseScale).setDepth(9).setAlpha(0);
      }
    }

    // Container (labels + border + hit zone, ON TOP of images)
    const container = this.add.container(x, y).setDepth(12);

    // Dark base fallback (if image didn't load)
    if (!imgNormal) {
      container.add(this.add.circle(0, 0, radius, 0x08080f, 1));
    }

    // Border (invisible for image portals, subtle for others)
    const border = this.add.graphics();
    if (!imgNormal) {
      border.lineStyle(1, cfg.color, 0.3);
      border.strokeCircle(0, 0, radius);
    }
    container.add(border);

    // Labels
    const label = this.add.text(0, -10, cfg.label, {
      fontFamily: 'monospace', fontSize: '24px', color: '#ffffff',
      fontStyle: 'bold', stroke: '#000000', strokeThickness: 5,
    }).setOrigin(0.5);
    container.add(label);

    container.add(this.add.text(0, 18, cfg.sub, {
      fontFamily: 'monospace', fontSize: '10px', color: '#888899',
      stroke: '#000000', strokeThickness: 3,
    }).setOrigin(0.5));

    // Breathing border (only for non-image portals)
    if (!imgNormal) {
      this.tweens.addCounter({
        from: 0, to: 1, duration: 2200, yoyo: true, repeat: -1, ease: 'Sine.easeInOut',
        onUpdate: (tw) => {
          const v = (tw.getValue() ?? 0) as number;
          border.clear();
          border.lineStyle(1, cfg.color, 0.2 + v * 0.15);
          border.strokeCircle(0, 0, radius);
        },
      });
    }

    // Hit zone
    const hit = this.add.circle(0, 0, radius, 0x000000, 0.001)
      .setInteractive({ useHandCursor: true });
    container.add(hit);

    const portal: PortalEntry = { x, y, radius, container, glowGfx, imgNormal, imgGlow, baseScale, color: cfg.color };

    // --- HOVER ---
    hit.on('pointerover', () => {
      sfx(this, 'menu_portal_hover');
      // Lift all elements
      this.tweens.add({ targets: container, y: y - 8, duration: 250, ease: 'Power2' });
      if (imgNormal) this.tweens.add({ targets: imgNormal, y: y - 8, duration: 250, ease: 'Power2' });
      if (imgGlow) this.tweens.add({ targets: imgGlow, y: y - 8, duration: 250, ease: 'Power2' });

      // Show glow behind
      this.tweens.add({ targets: glowGfx, alpha: 1, duration: 300 });

      // Transition normal → glow image with slight zoom (stays inside circle since images are pre-clipped)
      // Transition normal → glow (no zoom overflow — stays inside circle)
      if (imgNormal) {
        this.tweens.add({ targets: imgNormal, alpha: 0, duration: 400, ease: 'Power2' });
      }
      if (imgGlow) {
        this.tweens.add({ targets: imgGlow, alpha: 1, duration: 400, ease: 'Power2' });
      }

      label.setColor('#ffd700');

      // Dim others
      this.portals.forEach((p, pi) => {
        if (pi !== index) {
          this.tweens.add({ targets: p.container, alpha: 0.3, duration: 250 });
          if (p.imgNormal) this.tweens.add({ targets: p.imgNormal, alpha: 0.25, duration: 250 });
          if (p.imgGlow) this.tweens.add({ targets: p.imgGlow, alpha: 0, duration: 250 });
          this.tweens.add({ targets: p.glowGfx, alpha: 0, duration: 200 });
        }
      });
    });

    hit.on('pointerout', () => {
      // Return
      this.tweens.add({ targets: container, y: y, duration: 250 });
      if (imgNormal) this.tweens.add({ targets: imgNormal, y: y, alpha: 1, scaleX: baseScale, scaleY: baseScale, duration: 300 });
      if (imgGlow) this.tweens.add({ targets: imgGlow, y: y, alpha: 0, scaleX: baseScale, scaleY: baseScale, duration: 300 });
      this.tweens.add({ targets: glowGfx, alpha: 0, duration: 300 });

      label.setColor('#ffffff');

      // Restore others
      this.portals.forEach((p) => {
        this.tweens.add({ targets: p.container, alpha: 1, duration: 250 });
        if (p.imgNormal) this.tweens.add({ targets: p.imgNormal, alpha: 1, duration: 250 });
        if (p.imgGlow) this.tweens.add({ targets: p.imgGlow, alpha: 0, duration: 250 });
      });
    });

    hit.on('pointerdown', () => {
      sfx(this, 'menu_portal_click');
      if (index === 0) this.clickPlay(portal);
      if (index === 1) this.clickSkins(portal);
      if (index === 2) this.clickAgents(portal);
    });

    return portal;
  }

  private clickSkins(p: PortalEntry): void {
    sfx(this, 'scene_transition');
    this.cameras.main.flash(150, 157, 0, 255, false);
    this.cameras.main.shake(100, 0.005);

    this.tweens.add({ targets: p.container, scaleX: 3, scaleY: 3, alpha: 0, duration: 500, ease: 'Power3' });
    if (p.imgNormal) this.tweens.add({ targets: p.imgNormal, scaleX: p.baseScale * 4, scaleY: p.baseScale * 4, alpha: 0, duration: 500 });

    this.portals.forEach(o => {
      if (o !== p) {
        this.tweens.add({ targets: o.container, alpha: 0, duration: 200 });
        if (o.imgNormal) this.tweens.add({ targets: o.imgNormal, alpha: 0, duration: 200 });
      }
    });

    this.time.delayedCall(400, () => {
      this.cameras.main.fadeOut(300, 0, 0, 0);
      this.cameras.main.once('camerafadeoutcomplete', () => {
        this.scene.start('SkinSelectScene');
      });
    });
  }

  private clickAgents(p: PortalEntry): void {
    track('agent_info_clicked');
    sfx(this, 'scene_transition');
    this.cameras.main.flash(150, 0, 200, 255, false);
    this.cameras.main.shake(100, 0.005);

    this.tweens.add({ targets: p.container, scaleX: 3, scaleY: 3, alpha: 0, duration: 500, ease: 'Power3' });
    if (p.imgNormal) this.tweens.add({ targets: p.imgNormal, scaleX: p.baseScale * 4, scaleY: p.baseScale * 4, alpha: 0, duration: 500 });

    this.portals.forEach(o => {
      if (o !== p) {
        this.tweens.add({ targets: o.container, alpha: 0, duration: 200 });
        if (o.imgNormal) this.tweens.add({ targets: o.imgNormal, alpha: 0, duration: 200 });
      }
    });

    this.time.delayedCall(400, () => {
      this.cameras.main.fadeOut(300, 0, 0, 0);
      this.cameras.main.once('camerafadeoutcomplete', () => {
        this.scene.start('AgentInfoScene');
      });
    });
  }

  private clickPlay(p: PortalEntry): void {
    trackPlayButtonClicked();
    sfx(this, 'scene_transition');
    // Probe latency NOW, in parallel with the portal juice below. Timing
    // a fetch to the server captures the player's RTT plus any event-loop
    // saturation, so a high result means "this match will feel bad". The
    // promise resolves (~5 probes) right around when the animation ends,
    // so the check is hidden behind the burst rather than adding a pause.
    const latencyPromise = measureLatency();
    this.cameras.main.flash(200, 255, 215, 0, false);
    this.cameras.main.shake(150, 0.008);

    this.tweens.add({ targets: p.container, scaleX: 4, scaleY: 4, alpha: 0, duration: 600, ease: 'Power3' });
    if (p.imgNormal) this.tweens.add({ targets: p.imgNormal, scaleX: p.baseScale * 5, scaleY: p.baseScale * 5, alpha: 0, duration: 600 });
    if (p.imgGlow) this.tweens.add({ targets: p.imgGlow, scaleX: p.baseScale * 5, scaleY: p.baseScale * 5, alpha: 0, duration: 600 });

    this.portals.forEach(o => {
      if (o !== p) {
        this.tweens.add({ targets: o.container, alpha: 0, duration: 200 });
        if (o.imgNormal) this.tweens.add({ targets: o.imgNormal, alpha: 0, duration: 200 });
      }
    });

    if (!this.textures.exists('menu_burst')) {
      const g = this.add.graphics();
      g.fillStyle(0xffffff, 1);
      g.fillRect(0, 0, 3, 3);
      g.generateTexture('menu_burst', 3, 3);
      g.destroy();
    }
    const burst = this.add.particles(p.x, p.y, 'menu_burst', {
      speed: { min: 80, max: 250 }, angle: { min: 0, max: 360 },
      scale: { start: 1.5, end: 0 }, alpha: { start: 1, end: 0 },
      lifespan: 500, quantity: 35, tint: [p.color, 0xffffff],
      blendMode: 'ADD', emitting: false,
    }).setDepth(30);
    burst.explode(35);

    // Let the burst/portal animation play, then act on the lag probe.
    this.time.delayedCall(400, () => {
      void latencyPromise.then((ms) => {
        if (ms >= LAG_WARN_MS) {
          track('lag_warning_shown', { ms: Number.isFinite(ms) ? Math.round(ms) : -1 });
          this.showLagWarning(ms);
        } else {
          this.proceedToGame();
        }
      });
    });
  }

  /** Fade out and route to the perk/payment screen. */
  private proceedToGame(): void {
    this.cameras.main.fadeOut(300, 0, 0, 0);
    this.cameras.main.once('camerafadeoutcomplete', () => {
      this.routeToGame({ perks: this.getRandomPerks() });
    });
  }

  /** Modal shown when the pre-play lag probe is over the threshold. The
   *  player decides: PLAY ANYWAY proceeds, CANCEL restarts the menu (a
   *  clean way to restore the portals the click animation tweened away).
   *  We only warn — never hard-block — so a player on a known-slow link
   *  can still choose to play. */
  private showLagWarning(ms: number): void {
    const { width, height } = this.scale;
    const reachable = Number.isFinite(ms);
    const depth = 1000;

    const overlay = this.add.container(0, 0).setDepth(depth);

    // Full-screen dim that also eats clicks behind the panel.
    const dim = this.add.rectangle(0, 0, width, height, 0x000000, 0.72)
      .setOrigin(0, 0)
      .setInteractive();

    const panelW = Math.min(560, width * 0.86);
    const panelH = 280;
    const panel = this.add.rectangle(width / 2, height / 2, panelW, panelH, 0x141821, 0.98)
      .setStrokeStyle(2, 0xffd23f);

    const title = this.add.text(width / 2, height / 2 - panelH / 2 + 36, 'HIGH LAG DETECTED', {
      fontFamily: 'monospace', fontSize: '24px', color: '#ffd23f', fontStyle: 'bold',
    }).setOrigin(0.5);

    const bodyText = reachable
      ? `Your connection to the server is slow (${Math.round(ms)} ms).\nThe match may feel choppy — playing is not\nrecommended right now.`
      : `Can't reach the game server right now.\nThe match may not work — playing is not\nrecommended.`;
    const body = this.add.text(width / 2, height / 2 - 8, bodyText, {
      fontFamily: 'monospace', fontSize: '15px', color: '#c8d0dc', align: 'center', lineSpacing: 6,
    }).setOrigin(0.5);

    const mkButton = (
      cx: number, label: string, bg: number, fg: string, onClick: () => void,
    ): Phaser.GameObjects.GameObject[] => {
      const by = height / 2 + panelH / 2 - 44;
      const btn = this.add.rectangle(cx, by, 200, 48, bg)
        .setInteractive({ useHandCursor: true });
      const txt = this.add.text(cx, by, label, {
        fontFamily: 'monospace', fontSize: '16px', color: fg, fontStyle: 'bold',
      }).setOrigin(0.5);
      btn.on('pointerover', () => btn.setAlpha(0.85));
      btn.on('pointerout', () => btn.setAlpha(1));
      btn.on('pointerdown', () => { sfx(this, 'menu_portal_click'); onClick(); });
      return [btn, txt];
    };

    const cancelBtn = mkButton(width / 2 - 110, 'CANCEL', 0x2a2f3a, '#c8d0dc', () => {
      track('lag_warning_choice', { choice: 'cancel', ms: reachable ? Math.round(ms) : -1 });
      this.scene.restart();
    });
    const playBtn = mkButton(width / 2 + 110, 'PLAY ANYWAY', 0xffd23f, '#141821', () => {
      track('lag_warning_choice', { choice: 'play_anyway', ms: reachable ? Math.round(ms) : -1 });
      overlay.destroy();
      this.proceedToGame();
    });

    overlay.add([dim, panel, title, body, ...cancelBtn, ...playBtn]);
  }

  /** Gate the transition to PerkSelectScene through the How-to-Play overlay
   *  for first-time / demo players. Centralized so PLAY and TRY DEMO share
   *  one rule. */
  private routeToGame(perkData: { perks: PerkDefinition[]; demoMode?: boolean }): void {
    if (this.shouldShowHowToPlay(!!perkData.demoMode)) {
      this.scene.start('HowToPlayScene', { next: 'PerkSelectScene', nextData: perkData });
    } else {
      this.scene.start('PerkSelectScene', perkData);
    }
  }

  /** Demo always sees the tutorial. Pago shows it for the first 2 sessions
   *  (localStorage). `?howto=0` opts out, `?howto=1` forces. */
  private shouldShowHowToPlay(demoMode: boolean): boolean {
    const params = new URLSearchParams(window.location.search);
    if (params.get('howto') === '0') return false;
    if (params.get('howto') === '1' || demoMode) return true;
    try {
      const count = Number(localStorage.getItem('snake_howto_count') || '0');
      return count < 2;
    } catch {
      return true;
    }
  }

  private getRandomPerks(): PerkDefinition[] {
    return getRandomPerks(3);
  }

  /** Download menu music in the background after the scene renders.
   *  When the first track lands, kick playMusic again so it actually
   *  starts (the call in create() ran before any track was cached and
   *  was a silent no-op). Subsequent tracks just enter the rotation
   *  pool and are picked when Music.ts auto-advances. */
  private lazyLoadMenuMusic(): void {
    const tracks = ['menu_sand1', 'menu_sand2', 'menu_sand3'];
    const toLoad = tracks.filter(k => !this.cache.audio.exists(k));
    if (toLoad.length === 0) return;

    let firstStarted = false;
    const onComplete = (key: string) => {
      if (!toLoad.includes(key)) return;
      if (!firstStarted) {
        firstStarted = true;
        playMusic(this.game, 'menu');
      }
    };
    this.load.on(Phaser.Loader.Events.FILE_COMPLETE, onComplete);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.load.off(Phaser.Loader.Events.FILE_COMPLETE, onComplete);
    });

    for (const key of toLoad) {
      this.load.audio(key, `music/${key}.mp3`);
    }
    this.load.start();
  }
}
